import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Flame, Shield, Clock, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import heatingImage from "@/assets/real-heating.jpg";

export const HeatingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Professional Heating Services - Gas Safe Engineers | Sortable"
        description="Find Gas Safe registered heating engineers near you. Boiler installation, repair, and servicing. Central heating systems, radiator installation, and emergency callouts across the UK."
        keywords="heating engineer, gas safe engineer, boiler repair, boiler installation, boiler service, central heating, radiator installation, gas safety certificate, boiler breakdown, emergency heating, heating system maintenance, combi boiler, system boiler, gas appliance service, heating system design, power flushing, thermostat installation, underfloor heating, gas safe registered"
        canonical="https://sortable.uk/services/heating"
        ogImage="https://sortable.uk/real-heating.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional Heating Services",
          "description": "Heating and gas services by Gas Safe registered engineers",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "serviceType": "Heating Services",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          }
        }}
      />

      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Home</span>
                  <span className="md:hidden">Home</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Tradesmen</span>
                  <span className="md:hidden">Tradesmen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-orange-600/10 border border-orange-600/20 text-orange-600 px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Flame className="h-5 w-5" />
            Professional Heating Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Gas Safe Heating Engineers
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            From boiler breakdowns to central heating installations, find Gas Safe registered heating engineers for all your heating and gas appliance needs. Emergency callouts available 24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/create-job">
              <Button size="lg" className="bg-gradient-hero hover:shadow-glow transform hover:scale-105">
                <Users className="mr-2 h-5 w-5" />
                Find Heating Engineers
              </Button>
            </Link>
            <Link to="/for-tradesmen">
              <Button size="lg" variant="outline">
                <Flame className="mr-2 h-5 w-5" />
                Join as Heating Engineer
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Complete Heating Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our network of Gas Safe registered engineers provides comprehensive heating solutions for domestic and commercial properties.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Emergency Boiler Repair",
                description: "24/7 emergency boiler breakdown and repair services",
                icon: <Clock className="w-6 h-6" />
              },
              {
                title: "Boiler Installation",
                description: "New boiler installation with warranties and aftercare support",
                icon: <Flame className="w-6 h-6" />
              },
              {
                title: "Central Heating Systems",
                description: "Complete central heating design, installation, and upgrades",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Annual Boiler Service",
                description: "Regular boiler servicing to maintain efficiency and safety",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Radiator Installation",
                description: "New radiator installation and heating system upgrades",
                icon: <Flame className="w-6 h-6" />
              },
              {
                title: "Gas Safety Certificates",
                description: "Annual gas safety inspections and certification services",
                icon: <Shield className="w-6 h-6" />
              }
            ].map((service, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-600/10 rounded-lg text-orange-600">
                      {service.icon}
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Heating Network?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              All our heating engineers are Gas Safe registered and committed to the highest safety standards.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Gas Safe Registered</h4>
                    <p className="text-muted-foreground">
                      All engineers are Gas Safe registered and qualified to work on gas appliances safely.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Manufacturer Certified</h4>
                    <p className="text-muted-foreground">
                      Certified to work on all major boiler brands including Worcester Bosch, Vaillant, and Baxi.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">24/7 Emergency Service</h4>
                    <p className="text-muted-foreground">
                      Emergency heating engineers available for urgent boiler breakdowns and gas emergencies.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Warranty & Support</h4>
                    <p className="text-muted-foreground">
                      Extended warranties on installations and ongoing support for all heating systems.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={heatingImage} 
                alt="Professional heating engineer working on boiler"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need a Heating Engineer Today?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Post your heating job and get quotes from Gas Safe registered engineers. Emergency repairs, installations, and servicing available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-job">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-3 border-white"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Get Quotes Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground mt-4 max-w-md">
                Connecting you with Gas Safe registered heating engineers across the UK.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>Emergency Repairs</li>
                <li>Boiler Installation</li>
                <li>Central Heating</li>
                <li>Gas Safety Certificates</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/create-job" className="hover:text-primary">Post a Job</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Engineers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Our Network</Link></li>
                <li><Link to="/tradesman-registration" className="hover:text-primary">Register Now</Link></li>
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};